import { useState, useEffect, useCallback } from 'react';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import type { PublisherMember, PublisherMemberRole } from 'types/publisher';
import {
  getPublisherMembers,
  addPublisherMember,
  updatePublisherMemberRole,
  removePublisherMember,
} from '../api/marketplaceApi';

export interface UsePublisherMembersResult {
  members: PublisherMember[];
  callerRole: PublisherMemberRole | null;
  callerIdentityId: string | null;
  loading: boolean;
  /** True when the initial member list failed to load. Callers render their own localized message. */
  loadFailed: boolean;
  isMutating: boolean;
  refetch: () => void;
  add: (identityId: string, role: 'manager' | 'creator') => Promise<boolean>;
  updateRole: (identityId: string, role: 'manager' | 'creator') => Promise<boolean>;
  remove: (identityId: string) => Promise<boolean>;
}

export function usePublisherMembers(publisherId: string | null): UsePublisherMembersResult {
  const apiClients = useApiClientsOrNull();

  const [members, setMembers] = useState<PublisherMember[]>([]);
  const [callerRole, setCallerRole] = useState<PublisherMemberRole | null>(null);
  const [callerIdentityId, setCallerIdentityId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const refetch = useCallback(() => {
    setFetchTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!apiClients || !publisherId) {
      setMembers([]);
      setCallerRole(null);
      setCallerIdentityId(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);

    getPublisherMembers(apiClients.schemaClient, publisherId)
      .then((res) => {
        if (!cancelled) {
          setMembers(res.members);
          setCallerRole(res.callerRole);
          setCallerIdentityId(res.callerIdentityId);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [apiClients, publisherId, fetchTrigger]);

  const add = useCallback(
    async (identityId: string, role: 'manager' | 'creator'): Promise<boolean> => {
      if (!apiClients || !publisherId) return false;

      setIsMutating(true);

      try {
        const res = await addPublisherMember(apiClients.schemaClient, publisherId, identityId, role);
        setMembers((prev) => [...prev, res.member]);
        return true;
      } catch {
        return false;
      } finally {
        setIsMutating(false);
      }
    },
    [apiClients, publisherId]
  );

  const updateRole = useCallback(
    async (identityId: string, role: 'manager' | 'creator'): Promise<boolean> => {
      if (!apiClients || !publisherId) return false;

      setIsMutating(true);

      const previous = [...members];
      const optimistic = members.map((m) =>
        m.identityId === identityId ? { ...m, role } : m
      );
      setMembers(optimistic);

      try {
        await updatePublisherMemberRole(apiClients.schemaClient, publisherId, identityId, role);
        return true;
      } catch {
        setMembers(previous);
        return false;
      } finally {
        setIsMutating(false);
      }
    },
    [apiClients, publisherId, members]
  );

  const remove = useCallback(
    async (identityId: string): Promise<boolean> => {
      if (!apiClients || !publisherId) return false;

      setIsMutating(true);

      const previous = [...members];
      const optimistic = members.filter((m) => m.identityId !== identityId);
      setMembers(optimistic);

      try {
        await removePublisherMember(apiClients.schemaClient, publisherId, identityId);
        return true;
      } catch {
        setMembers(previous);
        return false;
      } finally {
        setIsMutating(false);
      }
    },
    [apiClients, publisherId, members]
  );

  return {
    members,
    callerRole,
    callerIdentityId,
    loading,
    loadFailed,
    isMutating,
    refetch,
    add,
    updateRole,
    remove,
  };
}
